import { FC, useMemo } from 'react'

import styled from 'styled-components'

import { Checkbox } from '@style-port/roov-ui-components-react'
import { TableCell, Txt } from 'src/components/atoms'
import { TableSortableTxt, TableSortType } from 'src/components/molecules'

import { useAuthority } from 'src/hooks'
import { ExternalUserEntity, SelectableExternalUserEntity } from 'src/types'

const Container = styled.div`
  box-shadow: 0 5px 20px rgba(122, 137, 160, 0.1);
`

export type ExternalUserSortItemType = keyof Pick<
  ExternalUserEntity,
  'id' | 'name' | 'keywords' | 'email' | 'createdAt' | 'projectCount'
>

type Props = {
  externalUsers: SelectableExternalUserEntity[]
  sortItem: ExternalUserSortItemType
  sortType: TableSortType
  onSelectItem: (newItem: ExternalUserSortItemType) => void
  onClickAllSelect: () => void
  className?: string
}

export const ExternalUserListHead: FC<Props> = ({
  externalUsers,
  sortItem,
  sortType,
  onSelectItem,
  onClickAllSelect,
  className,
}) => {
  const { canAssignProject } = useAuthority()

  const isAllSelected = useMemo(() => externalUsers.every(user => user.isSelected), [externalUsers])

  const isSomeSelected = useMemo(() => externalUsers.some(user => user.isSelected), [externalUsers])

  const selectedUserCount = useMemo(
    () => externalUsers.filter(user => user.isSelected).length,
    [externalUsers]
  )

  return (
    <div className={className}>
      <Container className="tw-bg-surface-secondary tw-py-s-1">
        <div className="tw-flex tw-flex-nowrap tw-items-center">
          {canAssignProject && (
            <TableCell width={6}>
              <div className="tw-flex tw-flex-nowrap tw-items-center tw-ml-s0">
                <div role="presentation" onClick={onClickAllSelect}>
                  <Checkbox
                    nonce={undefined}
                    checked={isSomeSelected}
                    indeterminate={!isAllSelected && isSomeSelected}
                  />
                </div>
                <Txt sizeType="l" className="tw-font-bold tw-text-roovPrimary-default tw-ml-s-2">
                  {selectedUserCount}
                </Txt>
              </div>
            </TableCell>
          )}
          <TableCell width={6} align="center" className="tw-mr-s1">
            <TableSortableTxt
              text="ID"
              isActive={sortItem === 'id'}
              sortType={sortType}
              onSelect={() => onSelectItem('id')}
            />
          </TableCell>
          <TableCell width={22}>
            <TableSortableTxt
              text="お名前"
              isActive={sortItem === 'name'}
              sortType={sortType}
              onSelect={() => onSelectItem('name')}
            />
          </TableCell>
          <TableCell width={24}>
            <TableSortableTxt
              text="メールアドレス"
              isActive={sortItem === 'email'}
              sortType={sortType}
              onSelect={() => onSelectItem('email')}
            />
          </TableCell>
          <TableCell width={8} className="tw-mr-s1">
            <TableSortableTxt
              text="キーワード"
              isActive={sortItem === 'keywords'}
              sortType={sortType}
              onSelect={() => onSelectItem('keywords')}
            />
          </TableCell>
          <TableCell width={8} align="right" className="tw-mr-s1">
            <TableSortableTxt
              text="作成日"
              isActive={sortItem === 'createdAt'}
              sortType={sortType}
              onSelect={() => onSelectItem('createdAt')}
            />
          </TableCell>
          <TableCell align="right" className="tw-mr-s1">
            <TableSortableTxt
              text="プロジェクト数"
              isActive={sortItem === 'projectCount'}
              sortType={sortType}
              onSelect={() => onSelectItem('projectCount')}
            />
          </TableCell>
          {canAssignProject && <TableCell width={6} />}
        </div>
      </Container>
    </div>
  )
}
